import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import { ArrowLeft, ChevronDown, ChevronUp, Download, Search, Mail, FileText } from 'lucide-react';
import Spinner from './components/Spinner';
import httpClient from '@utils/httpClient';
import './Feedback360AdminResults.css';

interface ModuleResult {
  moduleId: number;
  moduleName: string;
  selfScore: number;
  managerAverage: number | null;
  teamAverage: number | null;
  overallAverage: number;
  totalQuestions: number;
}

interface SubjectiveItem {
  questionId: number;
  questionText: string;
  respondentName: string;
  textAnswer: string;
}

interface SubjectiveFeedback {
  participant: SubjectiveItem[];
  manager: SubjectiveItem[];
  reportee: SubjectiveItem[];
}

interface RaterLink {
  respondentType: 'manager' | 'reportee';
  name: string;
  email: string;
  link: string;
  responded: boolean;
}

interface ParticipantResult {
  userId: number;
  userName: string;
  userEmail: string;
  sessionId: number;
  sessionStatus: string;
  participantSubmittedAt: string;
  managerResponseCount: number;
  reporteeResponseCount: number;
  totalExpectedResponses: number;
  assessmentTitle?: string;
  moduleResults: ModuleResult[];
  subjectiveFeedback?: SubjectiveFeedback;
}

const SUBJECTIVE_GROUPS: { key: keyof SubjectiveFeedback; label: string; icon: string }[] = [
  { key: 'participant', label: 'Self', icon: '👤' },
  { key: 'manager', label: 'Manager(s)', icon: '👔' },
  { key: 'reportee', label: 'Team Members', icon: '👥' },
];

// Interpretation function
const getInterpretation = (score: number | null): { label: string; color: string; bg: string } | null => {
  if (score === null) return null;
  if (score >= 4.5 && score <= 5.0) return { label: "Strength", color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" };
  if (score >= 3.8 && score < 4.5) return { label: "Consistent Capability", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.1)" };
  if (score >= 3.0 && score < 3.8) return { label: "Situational / Inconsistent", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" };
  return { label: "Development Priority", color: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" };
};

const Feedback360AdminResults: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();

  const [participants, setParticipants] = useState<ParticipantResult[]>([]);
  const [expandedParticipant, setExpandedParticipant] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [downloadingSessionId, setDownloadingSessionId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [emailingSessionId, setEmailingSessionId] = useState<number | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [resendingSessionId, setResendingSessionId] = useState<number | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  // Per-session generated rater links, shown after a resend (so admin can share them without SMTP).
  const [resendLinks, setResendLinks] = useState<Record<number, RaterLink[]>>({});

  useEffect(() => {
    if (assessmentId) {
      fetchFeedback360Results();
    }
  }, [assessmentId]);

  const fetchFeedback360Results = async () => {
    if (!assessmentId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get(`/assessments/${assessmentId}/feedback-360-results`);
      setParticipants(data.data || []);
    } catch (err: any) {
      console.error('Error fetching feedback results:', err);
      setError(err.message || 'Failed to load feedback results');
    } finally {
      setLoading(false);
    }
  };

  const toggleParticipant = (userId: number) => {
    setExpandedParticipant(prev => prev === userId ? null : userId);
  };

  const downloadFeedbackPDF = async (sessionId: number, participantName: string) => {
    setDownloadingSessionId(sessionId);
    setDownloadError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/assessments/feedback-360/download-pdf/${sessionId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download PDF');
      }

      // Create blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `Feedback-${participantName.replace(/\s+/g, '_')}-${sessionId}.pdf`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      setDownloadError(err.message || 'Failed to download PDF');
    } finally {
      setDownloadingSessionId(null);
    }
  };

  const emailFeedbackPDF = async (sessionId: number, participantEmail: string) => {
    setEmailingSessionId(sessionId);
    setEmailSuccess(null);
    setDownloadError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/assessments/feedback-360/email-pdf/${sessionId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to email PDF');
      }

      const result = await response.json();
      setEmailSuccess(`Report successfully sent to ${participantEmail}`);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setEmailSuccess(null);
      }, 5000);

    } catch (err: any) {
      console.error('Error emailing PDF:', err);
      setDownloadError(err.message || 'Failed to email PDF');
    } finally {
      setEmailingSessionId(null);
    }
  };

  // (Re)generate manager/reportee feedback requests for a session. Used when raters were added
  // after the participant submitted (so the original submit created no tokens / expected=0).
  // Goes through the nucleus-api gateway (same base as the other admin feedback calls), which
  // forwards to assessment-api with the inter-service x-api-key.
  const regenerateRequests = async (sessionId: number) => {
    setResendingSessionId(sessionId);
    setResendError(null);

    try {
      const res: any = await httpClient.post(`/assessments/feedback-360/regenerate-requests/${sessionId}`);
      const links: RaterLink[] = res?.data?.links ?? [];
      setResendLinks(prev => ({ ...prev, [sessionId]: links }));
      // Refresh so the expected-response count and status reflect the newly created requests.
      await fetchFeedback360Results();
    } catch (err: any) {
      console.error('Error regenerating feedback requests:', err);
      setResendError(err.message || 'Failed to send feedback requests');
    } finally {
      setResendingSessionId(null);
    }
  };

  // Filter participants based on search query
  const filteredParticipants = participants.filter(participant =>
    participant.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    participant.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedParticipants = filteredParticipants.slice(startIndex, endIndex);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="feedback360-admin-results" data-theme={isDarkMode ? 'dark' : 'light'}>
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="feedback360-admin-results" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="error-message">{error}</div>
          <button className="btn-retry" onClick={fetchFeedback360Results}>
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="feedback360-admin-results" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="results-header">
          <button className="btn-back" onClick={() => navigate('/assessment-dashboard')}>
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
          <div className="header-row">
            <div className="header-content">
              <h1 className="results-title">{participants[0]?.assessmentTitle || 'Feedback'} Results</h1>
              <p className="results-subtitle">
                {searchQuery ? `Showing ${filteredParticipants.length} of ${participants.length} participants` : `Total Participants: ${participants.length}`}
              </p>
            </div>
            <button
              className="btn-consolidated"
              onClick={() => navigate(`/assessment-results/${assessmentId}/consolidated`)}
            >
              <FileText size={16} /> Consolidated Result
            </button>
          </div>
        </div>

        {/* Search Box */}
        <div className="search-container">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="interpretation-legend">
          <h3>Score Interpretation Guide</h3>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-badge" style={{ background: "rgba(16, 185, 129, 0.1)", color: "#10b981", border: "2px solid #10b981" }}>
                4.5 – 5.0
              </div>
              <div>
                <span>Strength</span>
                <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0 0", lineHeight: "1.4" }}>
                  Demonstrates this competency consistently at a high level and can be leveraged as a role model or strength area.
                </p>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-badge" style={{ background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6", border: "2px solid #3b82f6" }}>
                3.8 – 4.4
              </div>
              <div>
                <span>Consistent Capability</span>
                <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0 0", lineHeight: "1.4" }}>
                  Shows reliable and effective use of this competency in most situations, with opportunities to further refine impact.
                </p>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-badge" style={{ background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b", border: "2px solid #f59e0b" }}>
                3.0 – 3.7
              </div>
              <div>
                <span>Situational / Inconsistent</span>
                <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0 0", lineHeight: "1.4" }}>
                  Displays this competency unevenly across situations, indicating a need for greater awareness and consistency.
                </p>
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-badge" style={{ background: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "2px solid #ef4444" }}>
                &lt; 3.0
              </div>
              <div>
                <span>Development Priority</span>
                <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0 0", lineHeight: "1.4" }}>
                  Limited demonstration of this competency is observed, making it an important focus area for targeted development.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Participants List */}
        <div className="participants-container">
          {filteredParticipants.length === 0 ? (
            <div className="empty-state">
              <p>{searchQuery ? 'No participants found matching your search.' : 'No feedback results available yet.'}</p>
              <p className="empty-subtitle">
                {searchQuery ? 'Try adjusting your search terms.' : 'Results will appear here once participants have submitted their feedback and all responses have been received.'}
              </p>
            </div>
          ) : (
            paginatedParticipants.map((participant) => (
              <div key={participant.userId} className="participant-card">
                <div className="participant-header" onClick={() => toggleParticipant(participant.userId)}>
                  <div className="participant-info">
                    <h3>{participant.userName}</h3>
                    <p className="participant-email">{participant.userEmail}</p>
                    <div className="participant-meta">
                      <span className="meta-item">
                        Submitted: {new Date(participant.participantSubmittedAt).toLocaleDateString()}
                      </span>
                      <span className="meta-item">
                        Responses: {participant.managerResponseCount + participant.reporteeResponseCount} / {participant.totalExpectedResponses}
                      </span>
                      <span className={`status-badge ${participant.sessionStatus}`}>
                        {participant.sessionStatus === 'completed' ? '✓ Complete' : '⏳ Pending'}
                      </span>
                    </div>
                  </div>
                  <button className="expand-btn">
                    {expandedParticipant === participant.userId ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </button>
                </div>

                {expandedParticipant === participant.userId && participant.sessionStatus === 'completed' && (
                  <div className="participant-details">
                    <div className="response-summary">
                      <div className="response-box">
                        <div className="response-icon">👤</div>
                        <div>
                          <div className="response-label">Self</div>
                          <div className="response-value">1</div>
                        </div>
                      </div>
                      <div className="response-box">
                        <div className="response-icon">👔</div>
                        <div>
                          <div className="response-label">Managers</div>
                          <div className="response-value">{participant.managerResponseCount}</div>
                        </div>
                      </div>
                      <div className="response-box">
                        <div className="response-icon">👥</div>
                        <div>
                          <div className="response-label">Team Members</div>
                          <div className="response-value">{participant.reporteeResponseCount}</div>
                        </div>
                      </div>
                    </div>

                    {/* Module Results Table */}
                    <table className="modules-table">
                      <thead>
                        <tr>
                          <th>Module</th>
                          <th style={{ textAlign: 'center' }}>Self</th>
                          <th style={{ textAlign: 'center' }}>Manager(s)</th>
                          <th style={{ textAlign: 'center' }}>Team</th>
                          <th style={{ textAlign: 'center' }}>⭐ Overall</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participant.moduleResults.map((module) => {
                          const selfInterpretation = getInterpretation(module.selfScore);
                          const managerInterpretation = getInterpretation(module.managerAverage);
                          const teamInterpretation = getInterpretation(module.teamAverage);
                          const overallInterpretation = getInterpretation(module.overallAverage);

                          return (
                            <tr key={module.moduleId}>
                              <td>
                                <div className="module-name">{module.moduleName}</div>
                                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                  {module.totalQuestions} questions
                                </div>
                              </td>
                              <td className="score-cell self">
                                <div>{module.selfScore.toFixed(2)}</div>
                                {selfInterpretation && (
                                  <div
                                    className="score-interpretation"
                                    style={{
                                      background: selfInterpretation.bg,
                                      color: selfInterpretation.color,
                                      border: `1px solid ${selfInterpretation.color}`
                                    }}
                                  >
                                    {selfInterpretation.label}
                                  </div>
                                )}
                              </td>
                              <td className="score-cell manager">
                                <div>
                                  {module.managerAverage !== null ? module.managerAverage.toFixed(2) : 'N/A'}
                                </div>
                                {managerInterpretation && (
                                  <div
                                    className="score-interpretation"
                                    style={{
                                      background: managerInterpretation.bg,
                                      color: managerInterpretation.color,
                                      border: `1px solid ${managerInterpretation.color}`
                                    }}
                                  >
                                    {managerInterpretation.label}
                                  </div>
                                )}
                              </td>
                              <td className="score-cell team">
                                <div>
                                  {module.teamAverage !== null ? module.teamAverage.toFixed(2) : 'N/A'}
                                </div>
                                {teamInterpretation && (
                                  <div
                                    className="score-interpretation"
                                    style={{
                                      background: teamInterpretation.bg,
                                      color: teamInterpretation.color,
                                      border: `1px solid ${teamInterpretation.color}`
                                    }}
                                  >
                                    {teamInterpretation.label}
                                  </div>
                                )}
                              </td>
                              <td className="score-cell overall">
                                <div>{module.overallAverage.toFixed(2)}</div>
                                {overallInterpretation && (
                                  <div
                                    className="score-interpretation"
                                    style={{
                                      background: overallInterpretation.bg,
                                      color: overallInterpretation.color,
                                      border: `2px solid ${overallInterpretation.color}`
                                    }}
                                  >
                                    {overallInterpretation.label}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Subjective (open-ended) feedback */}
                    {participant.subjectiveFeedback && SUBJECTIVE_GROUPS.some(
                      g => (participant.subjectiveFeedback?.[g.key]?.length ?? 0) > 0
                    ) && (
                      <div className="subjective-feedback-section" style={{ marginTop: '24px' }}>
                        <h3 style={{ fontSize: '16px', margin: '0 0 12px 0', color: isDarkMode ? '#e5e7eb' : '#1f2937' }}>
                          Open-Ended Feedback
                        </h3>
                        {SUBJECTIVE_GROUPS.map(group => {
                          const items = participant.subjectiveFeedback?.[group.key] ?? [];
                          if (items.length === 0) return null;
                          return (
                            <div key={group.key} style={{ marginBottom: '16px' }}>
                              <div style={{ fontWeight: 600, marginBottom: '8px', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                                {group.icon} {group.label}
                              </div>
                              {items.map((item, idx) => (
                                <div
                                  key={`${item.questionId}-${idx}`}
                                  style={{
                                    padding: '10px 14px',
                                    marginBottom: '8px',
                                    borderRadius: '8px',
                                    background: isDarkMode ? '#1f2937' : '#f9fafb',
                                    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                  }}
                                >
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#cbd5e1' : '#374151' }}>
                                    {item.questionText}
                                  </div>
                                  <div style={{ fontSize: '14px', marginTop: '4px', color: isDarkMode ? '#e5e7eb' : '#1f2937' }}>
                                    {item.textAnswer}
                                  </div>
                                  {item.respondentName && (
                                    <div style={{ fontSize: '12px', marginTop: '4px', color: '#9ca3af', fontStyle: 'italic' }}>
                                      — {item.respondentName}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="detail-actions">
                      <button
                        className="btn-download"
                        onClick={() => downloadFeedbackPDF(participant.sessionId, participant.userName)}
                        disabled={downloadingSessionId === participant.sessionId}
                      >
                        {downloadingSessionId === participant.sessionId ? (
                          <>
                            <div className="spinner-small"></div> Generating PDF...
                          </>
                        ) : (
                          <>
                            <Download size={16} /> Download PDF Report
                          </>
                        )}
                      </button>

                      <button
                        className="btn-email"
                        onClick={() => emailFeedbackPDF(participant.sessionId, participant.userEmail)}
                        disabled={emailingSessionId === participant.sessionId}
                      >
                        {emailingSessionId === participant.sessionId ? (
                          <>
                            <div className="spinner-small"></div> Sending Email...
                          </>
                        ) : (
                          <>
                            <Mail size={16} /> Email Report
                          </>
                        )}
                      </button>
                    </div>

                    {/* Success/Error Messages */}
                    {emailSuccess && (
                      <div className="email-success" style={{ color: '#10b981', marginTop: '12px', fontSize: '14px', fontWeight: '500' }}>
                        ✓ {emailSuccess}
                      </div>
                    )}
                    {downloadError && !emailingSessionId && !downloadingSessionId && (
                      <div className="download-error" style={{ color: '#ef4444', marginTop: '12px', fontSize: '14px' }}>
                        {downloadError}
                      </div>
                    )}
                  </div>
                )}

                {expandedParticipant === participant.userId && participant.sessionStatus !== 'completed' && (
                  <div className="participant-details">
                    <div className="pending-message">
                      <p>⏳ Waiting for all feedback responses</p>
                      <p className="pending-detail">
                        Received {participant.managerResponseCount + participant.reporteeResponseCount} out of {participant.totalExpectedResponses} responses
                      </p>
                    </div>

                    {participant.totalExpectedResponses === 0 && (
                      <p className="pending-detail" style={{ marginTop: '8px', color: '#f59e0b' }}>
                        No manager/team-member requests were generated for this participant (they may
                        have been added after submission). Use the button below to send the requests now.
                      </p>
                    )}

                    {/* Send / Resend feedback requests (creates missing manager/reportee links) */}
                    <div className="detail-actions" style={{ marginTop: '12px' }}>
                      <button
                        className="btn-email"
                        onClick={() => regenerateRequests(participant.sessionId)}
                        disabled={resendingSessionId === participant.sessionId}
                      >
                        {resendingSessionId === participant.sessionId ? (
                          <>
                            <div className="spinner-small"></div> Sending Requests...
                          </>
                        ) : (
                          <>
                            <Mail size={16} /> Send / Resend Feedback Requests
                          </>
                        )}
                      </button>
                    </div>

                    {resendError && resendingSessionId !== participant.sessionId && (
                      <div className="download-error" style={{ color: '#ef4444', marginTop: '12px', fontSize: '14px' }}>
                        {resendError}
                      </div>
                    )}

                    {/* Generated rater links (share manually when SMTP isn't configured) */}
                    {(resendLinks[participant.sessionId]?.length ?? 0) > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <h3 style={{ fontSize: '15px', margin: '0 0 8px 0', color: isDarkMode ? '#e5e7eb' : '#1f2937' }}>
                          Feedback Links
                        </h3>
                        {resendLinks[participant.sessionId].map((rater, idx) => (
                          <div
                            key={`${rater.email}-${idx}`}
                            style={{
                              padding: '10px 14px',
                              marginBottom: '8px',
                              borderRadius: '8px',
                              background: isDarkMode ? '#1f2937' : '#f9fafb',
                              border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                            }}
                          >
                            <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#cbd5e1' : '#374151' }}>
                              {rater.respondentType === 'manager' ? '👔 Manager' : '👥 Team Member'} · {rater.name} ({rater.email})
                              {rater.responded ? ' · ✓ responded' : ' · ⏳ awaiting response'}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
                              <input
                                type="text"
                                readOnly
                                value={rater.link}
                                style={{
                                  flex: 1,
                                  fontSize: '12px',
                                  padding: '6px 8px',
                                  borderRadius: '6px',
                                  border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                  background: isDarkMode ? '#111827' : '#ffffff',
                                  color: isDarkMode ? '#e5e7eb' : '#1f2937',
                                }}
                                onFocus={(e) => e.currentTarget.select()}
                              />
                              <button
                                type="button"
                                className="btn-email"
                                style={{ padding: '6px 10px', fontSize: '12px' }}
                                onClick={() => navigator.clipboard?.writeText(rater.link)}
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {filteredParticipants.length > 0 && totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {startIndex + 1} - {Math.min(endIndex, filteredParticipants.length)} of {filteredParticipants.length} participants
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Feedback360AdminResults;
